import { useCallback, useEffect, useRef, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { IconDownload } from "../../../components/Icons";

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

// Human-readable labels for the document types DomainAvailableDocuments emits
// on the backend. Anything not listed here falls back to the raw token so
// adding a new type only requires a tweak on the backend enum + (optionally)
// a label here — the dropdown keeps working in the meantime.
const DOCUMENT_LABELS: Record<string, string> = {
  PROMO_CONDITIONS: "Condiciones promo",
  FR_PREQUOTE: "Pre-cotización (FR)",
  TEN_YEAR_FORECAST: "Forecast 10 años",
  FIRST_YEAR_BREAKDOWN: "Desglose primer año",
  CERTIFICAT_DADHESION: "Certificat d'adhésion (FR)",
};

type Props = {
  leadUlid: string;
  token: string | null;
  // The legacy panel hides the Documents card for these statuses; we mirror
  // the rule so canceled / claim / rejected leads don't surface stale
  // generators that would error on every click.
  leadStatus: string;
};

const HIDDEN_FOR_STATUS = new Set(["canceled", "claim", "rejected"]);

export default function DocumentDownloadButton({
  leadUlid,
  token,
  leadStatus,
}: Props) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingType, setDownloadingType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const hidden = HIDDEN_FOR_STATUS.has(leadStatus);

  const loadAvailable = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<{ documents: string[] }>(
        `/api/admin/operations/leads/${leadUlid}/available-documents`,
        { token },
      );
      setAvailable(data.documents);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  // Lazy-load on first open: ops typically doesn't click into the Documents
  // menu, so we avoid the extra round-trip on every lead detail render.
  useEffect(() => {
    if (open && null === available && !loading) {
      void loadAvailable();
    }
  }, [open, available, loading, loadAvailable]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleDownload = async (documentType: string) => {
    setDownloadingType(documentType);
    setError(null);
    try {
      // Bypass the JSON helper because the endpoint streams a PDF binary;
      // fetch + blob + click is the same pattern the broker invoices tab uses.
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/documents/${documentType}/download`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) {
        throw new HttpError(response.status, await response.text());
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Server sets Content-Disposition with the canonical filename, but
      // it's not exposed via the Blob path — synthesize a fallback name
      // from the type + ulid so the download is recognisable.
      const fallbackName = `${documentType}-${leadUlid}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setOpen(false);
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as { message?: string } | undefined;
        setError(body?.message ?? `HTTP ${err.status}`);
      } else {
        setError("No se pudo descargar");
      }
    } finally {
      setDownloadingType(null);
    }
  };

  if (hidden) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Descargar documentos disponibles para el lead"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
          padding: "0.4rem 0.7rem",
          borderRadius: "0.4rem",
          background: open ? "#0069d9" : "#f8f9fb",
          color: open ? "white" : "#1f2933",
          border: `1px solid ${open ? "#0069d9" : "#dee2e6"}`,
          fontSize: ".78rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <IconDownload style={{ width: ".95rem", height: ".95rem" }} />
        <span>Documentos</span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 30,
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 16px rgba(0,0,0,.12)",
            minWidth: 240,
            padding: "0.4rem",
          }}
        >
          <div
            style={{
              fontSize: ".7rem",
              textTransform: "uppercase",
              color: "#73879C",
              fontWeight: 600,
              padding: "0.3rem 0.5rem 0.4rem",
            }}
          >
            Descargar documento
          </div>
          {loading && (
            <div
              style={{
                padding: "0.5rem",
                fontSize: ".78rem",
                color: "#73879C",
              }}
            >
              Cargando…
            </div>
          )}
          {!loading && available && available.length === 0 && (
            <div
              style={{
                padding: "0.5rem",
                fontSize: ".78rem",
                color: "#73879C",
              }}
            >
              Sin documentos disponibles para este lead.
            </div>
          )}
          {!loading &&
            available?.map((documentType) => (
              <button
                key={documentType}
                type="button"
                onClick={() => handleDownload(documentType)}
                disabled={!!downloadingType}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "0.4rem 0.5rem",
                  borderRadius: "0.35rem",
                  background: "transparent",
                  color: "#1f2933",
                  border: "none",
                  fontSize: ".82rem",
                  fontWeight: 500,
                  cursor: downloadingType ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!downloadingType) e.currentTarget.style.background = "#f8f9fb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {downloadingType === documentType
                  ? "Generando…"
                  : DOCUMENT_LABELS[documentType] ?? documentType}
              </button>
            ))}
          {error && (
            <div
              style={{
                padding: "0.4rem 0.5rem",
                fontSize: ".72rem",
                color: "#b32424",
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
